/**
 * Tool condition, faults and diagnosis — generalised across every module.
 *
 * The etch module grew a fault system first: a chamber state the recipe does
 * not control, faults that perturb it, and a diagnosis that restores one aspect
 * at a time to find the cause. All of that was etch-shaped - wall seasoning,
 * focus rings, RF match - so no other tool could use it.
 *
 * What is actually general is the SHAPE of the problem, and it is the same on
 * every tool in the fab:
 *
 *   a condition the operator does not set and cannot fully see
 *   a set of independently repairable aspects of it
 *   faults that perturb the condition and reach the wafer only through physics
 *   bench readings, some free and some that have to be earned
 *   a diagnosis: restore one aspect, change nothing else, see what recovers
 *
 * A module supplies a `ConditionModel` describing its own version of that, and
 * gets the diagnosis, the shift report and the scoring for free.
 */

/** One measurement a technician can take at the tool. */
export interface ToolReading {
  id: string
  label: string
  value: string
  /** What it should be, for display next to the value. */
  limit?: string
  state?: 'good' | 'warning' | 'critical'
}

/** An independently repairable part of the tool. */
export interface ToolAspect<A extends string> {
  id: A
  label: string
  /** What fixing it actually involves. */
  action: string
  /** Tool downtime the repair costs [hours]. A wrong call spends this twice. */
  downtimeHours: number
  /**
   * Ids of the readings that bear on this aspect.
   *
   * This exists because the counterfactual is not always decisive. Wall
   * deposits detune the match, so on the etcher a chamber clean recovers 86% of
   * a detuned match and a match tune recovers 94% - the two repairs are barely
   * distinguishable by result, and a student who reasons only from "what would
   * fixing this recover" is being asked to guess.
   *
   * The readings are what break that tie: a detuned match shows low delivered
   * power with a perfectly normal wafer count since the last clean. So the
   * harness requires that where recovery cannot separate two candidates, the
   * bench readings can - and it needs this mapping to check it.
   */
  readingIds?: string[]
}

export interface ToolFault<A extends string, C> {
  id: string
  label: string
  /** What a technician is told, with no diagnosis attached. */
  complaint: string
  /** What is actually wrong. Shown only after the student commits. */
  truth: string
  /** Evidence that confirms it, and evidence that rules out the lookalikes. */
  evidence: string[]
  /** The aspect this fault lives in. Null for a healthy tool. */
  aspect: A | null
  /**
   * The tool is broken and the wafer still ships.
   *
   * This is not a weak fault, it is the hardest kind of shift to run, and it
   * has to be declared rather than discovered: a detuned match at 70% delivered
   * power costs a fifth of the etch rate while every wafer still passes, and a
   * degraded pump runs 12% fast. Nothing is scrapping, so nothing forces a
   * decision - and the tool goes on drifting until it does scrap something, at
   * which point the downtime is not four hours, it is four hours plus a lot.
   *
   * Declaring it here means the harness asserts the claim in BOTH directions:
   * a fault marked this way must stay in spec, and one not marked must leave
   * it. If a change to the physics moves a fault across that line, the harness
   * says so instead of quietly re-teaching the lesson.
   */
  shipsAnyway?: boolean
  /** Produce the faulted condition from a healthy one. */
  apply(nominal: C): C
}

/**
 * How a module describes its own condition. Everything the generic diagnosis
 * and the shift report need, and nothing module-specific leaks out.
 */
export interface ConditionModel<A extends string, C> {
  /**
   * The tool's registry id - `dicing`, `litho`, `gowning`.
   *
   * Not decoration and not a duplicate of `toolLabel`: it is the key this
   * tool's fault catalogue is filed under, so `tool.dicing.fault.glazedWheel`
   * resolves. `toolLabel` is prose and is itself translated; an id has to be
   * stable across languages or none of the lookups below would hold.
   */
  toolId: string
  /** Human name of the tool, for the report. */
  toolLabel: string
  nominal: C
  aspects: ToolAspect<A>[]
  faults: ToolFault<A, C>[]
  /**
   * Return exactly one aspect to nominal, leaving everything else as found.
   * This is the counterfactual the diagnosis is built from.
   */
  restore(condition: C, aspect: A): C
  /** Readings available at the bench without doing anything. */
  readings(condition: C): ToolReading[]
  /**
   * Readings that cost effort - a flow verification, an instrumented wafer, a
   * rate-of-rise test. Withheld until the student chooses to take them, because
   * the difference between a reading you have and one you have to earn is most
   * of what makes a diagnosis a decision.
   */
  earnedReadings(condition: C): ToolReading[]
  /**
   * Run the process under a condition and return the two numbers a diagnosis is
   * scored on.
   *
   * `primary` is whatever the tool is judged by - etch rate, growth per cycle,
   * base pressure. `secondary` is the axis that catches what the mean hides,
   * and it is deliberately NOT called uniformity: on the process tools it is
   * wafer non-uniformity, but on the vacuum system it is pump-down time and on
   * the metrology tool it is gauge repeatability. Calling it uniformity was the
   * first draft, and it forced the vacuum module to either invent a uniformity
   * it does not have or lose the only axis that shows a dying pump.
   */
  evaluate(condition: C): { primary: number; secondary: number; unit: string }
  /** What the secondary axis is on this tool, for the report. */
  secondaryLabel: string
  secondaryUnit: string
  /**
   * How much of a change in `secondary` counts as one unit of badness in the
   * deviation score. Per-tool because the units are per-tool: ten percentage
   * points of non-uniformity on an etcher, thirty seconds of pump-down on a
   * vacuum system.
   */
  secondaryScale: number
  /**
   * The same for `primary`. Optional, and the default matters.
   *
   * When it is absent the primary term is RELATIVE - measured against the
   * healthy value - which is the right instinct for a rate or a pressure, where
   * a fault means "20% slow" or "two decades high".
   *
   * It is the wrong instinct for a measurement. A scanner printing 96 nm where
   * it should print 90 is badly broken, and relative to 90 that is 0.07 - which
   * scored as almost nothing next to an etcher running half rate. Tools whose
   * primary metric is a dimension rather than a rate set this to their spec
   * half-width instead, so that "one spec width out" means the same number on
   * every tool in the fab.
   */
  primaryScale?: number
  /** Spec window, used to decide whether a wafer ships. */
  spec: { lo: number; hi: number; maxSecondary: number }
}

// --------------------------------------------------------------- diagnosis ---

/**
 * Distance from a healthy tool, in the two things a technician is judged on.
 *
 * The primary term is RELATIVE, which is what lets one function score an etch
 * rate near 200 and a base pressure near 1e-8 without either of them drowning
 * the other. The secondary term is divided by a per-tool scale so that a
 * meaningful change in it counts about the same as a doubling of the primary.
 *
 * Without that second term a fault that ruins every wafer while barely moving
 * the mean - a worn focus ring, a tilted stage - would be invisible to the
 * score. On the vacuum system it is worse than invisible: a pump can lose 95%
 * of its speed with the gauge reading perfectly, because base pressure on a dry
 * chamber is set by what is coming in, not by what is pumping it out.
 */
export function deviation(
  m: { primary: number; secondary: number },
  healthy: { primary: number; secondary: number },
  secondaryScale = 10,
  primaryScale?: number,
): number {
  const pScale = primaryScale ?? Math.max(Math.abs(healthy.primary), 1e-30)
  return (
    Math.abs(m.primary - healthy.primary) / pScale +
    Math.abs(m.secondary - healthy.secondary) / secondaryScale
  )
}

export interface AspectTrial<A extends string> {
  aspect: A
  label: string
  action: string
  downtimeHours: number
  primary: number
  secondary: number
  /** Fraction of the as-found deviation this single repair removed. */
  recovery: number
}

export interface Diagnosis<A extends string> {
  asFound: { primary: number; secondary: number; unit: string }
  healthy: { primary: number; secondary: number; unit: string }
  deviation: number
  /** Trials sorted best-recovery first. */
  ranked: AspectTrial<A>[]
  /** Gap between best and second best; below ~0.12 it is not decisive. */
  separation: number
}

/**
 * Restore each aspect in turn and see which single repair recovers the most.
 *
 * The same method a technician uses at the bench, and deliberately the only
 * implementation - the assistant intents and the validation harness both call
 * this, so a diagnosis cannot be scored one way in a test and another for a
 * student.
 */
export function diagnose<A extends string, C>(
  model: ConditionModel<A, C>,
  condition: C,
): Diagnosis<A> {
  const asFound = model.evaluate(condition)
  const healthy = model.evaluate(model.nominal)
  const scale = model.secondaryScale
  const pScale = model.primaryScale
  const found = deviation(asFound, healthy, scale, pScale)

  const ranked: AspectTrial<A>[] = model.aspects
    .map((a) => {
      const repaired = model.evaluate(model.restore(condition, a.id))
      const recovery =
        found > 1e-9 ? (found - deviation(repaired, healthy, scale, pScale)) / found : 0
      return {
        aspect: a.id,
        label: a.label,
        action: a.action,
        downtimeHours: a.downtimeHours,
        primary: repaired.primary,
        secondary: repaired.secondary,
        recovery,
      }
    })
    .sort((x, y) => y.recovery - x.recovery)

  return {
    asFound,
    healthy,
    deviation: found,
    ranked,
    separation: ranked.length > 1 ? ranked[0].recovery - ranked[1].recovery : 1,
  }
}

/** Does a wafer produced under this condition ship? */
export function inSpec<A extends string, C>(model: ConditionModel<A, C>, condition: C): boolean {
  const m = model.evaluate(condition)
  return (
    m.primary >= model.spec.lo && m.primary <= model.spec.hi && m.secondary <= model.spec.maxSecondary
  )
}

/**
 * What an earned reading costs, read out of its own label.
 *
 * The cost lives inside the label - "(90 min)", "(2 h)", "(5 days)" - rather
 * than in a separate field, so that a module author cannot write a reading
 * whose displayed cost and charged cost disagree. That kind of drift is how a
 * scoring system quietly stops meaning anything.
 *
 * The flip side is that a mistyped label would silently cost nothing, so the
 * fault harness asserts that every earned reading on every tool declares a cost
 * this function can actually find.
 *
 * A day is eight hours: the unit here is technician time, not wall clock.
 */
export function readingCostHours(label: string): number {
  // The label may be the catalogue's translation rather than the English data,
  // so the price tag has to parse in every language the catalogue carries -
  // fullwidth parentheses and CJK units included. A tag this parser cannot
  // read silently prices the reading at zero, which is why the i18n harness
  // asserts every translated label of a priced reading still parses.
  const m = /[(（](\d+(?:\.\d+)?)\s*(min|h|hours?|days?|d[ií]as?|分鐘|分钟|小時|小时|天)[)）]/i.exec(
    label,
  )
  if (!m) return 0
  const n = Number(m[1])
  const unit = m[2].toLowerCase()
  if (unit === 'min' || unit === '分鐘' || unit === '分钟') return n / 60
  if (unit.startsWith('day') || unit.startsWith('día') || unit.startsWith('dia') || unit === '天') {
    return n * 8
  }
  return n
}

/** Pick a fault at random, excluding the healthy one. */
export function randomFault<A extends string, C>(
  model: ConditionModel<A, C>,
  rng: () => number = Math.random,
): ToolFault<A, C> {
  const candidates = model.faults.filter((f) => f.aspect !== null)
  return candidates[Math.floor(rng() * candidates.length)] ?? model.faults[0]
}
